import { Form, FormInstance, Input, Radio } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import type { UploadFile } from 'antd/es/upload/interface';
import clsx from 'clsx';
import InlineFileUpload from 'components/InlineFileUpload';
import { MAX_UPLOAD_FILESIZE } from 'const/attachments';
import { getFieldRules } from 'const/formRules';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getValueFromEvent } from 'utils/files';

export interface BeneficiaryFormType {
  same_contact?: 'oui' | 'non';
  civilite: string;
  firstname: string;
  lastname: string;
  email: string;
  descriptionProjet: string;
  telephone: string;
  files?: UploadFile[];
}

export interface SiteContactInfo {
  lastname: string;
  firstname: string;
  email: string;
}

interface BeneficiaryFormProps extends FormProps<BeneficiaryFormType> {
  formInstance: FormInstance<BeneficiaryFormType>;
  siteContactInfo?: SiteContactInfo;
}

export default function BeneficiaryForm({
  formInstance,
  siteContactInfo,
  ...props
}: BeneficiaryFormProps) {
  const { t } = useTranslation('add_dossier_beneficiary');
  const sameContact = Form.useWatch('same_contact', formInstance);

  const handleSameContactChange = (value: 'oui' | 'non') => {
    if (value === 'oui' && siteContactInfo) {
      formInstance.setFieldsValue({
        firstname: siteContactInfo.firstname,
        lastname: siteContactInfo.lastname,
        email: siteContactInfo.email,
      });
    }
    if (value === 'non') {
      formInstance.setFieldsValue({
        firstname: undefined,
        lastname: undefined,
        email: undefined,
      });
    }
  };

  return (
    <Form<BeneficiaryFormType> form={formInstance} layout="vertical" {...props}>
      <Form.Item<BeneficiaryFormType>
        name="same_contact"
        label={t('same_contact')}
        required
        rules={getFieldRules(t, { required: true })}
      >
        <Radio.Group onChange={(e) => handleSameContactChange(e.target.value)}>
          <Radio value="oui">{t('global:yes')}</Radio>
          <Radio value="non"> {t('global:no')} </Radio>
        </Radio.Group>
      </Form.Item>
      <div className={clsx({ hidden: sameContact !== 'non' })}>
        <Form.Item<BeneficiaryFormType>
          name="civilite"
          required={sameContact === 'non'}
          label={t('civilite')}
          rules={getFieldRules(t, { required: sameContact === 'non' })}
        >
          <Radio.Group>
            <Radio value="M.">{t('global:mr')}</Radio>
            <Radio value="Mme"> {t('global:mrs')} </Radio>
          </Radio.Group>
        </Form.Item>
        {(['firstname', 'lastname', 'email', 'telephone'] as const).map(
          (item) => (
            <Form.Item<BeneficiaryFormType>
              key={item}
              name={item}
              label={t(item)}
              required={sameContact === 'non'}
              rules={getFieldRules(t, {
                required: sameContact === 'non',
                email: item === 'email',
                phone: item === 'telephone',
                text_only: item === 'firstname' || item === 'lastname',
              })}
            >
              <Input placeholder={t(`${item}_placeholder`)} />
            </Form.Item>
          ),
        )}
      </div>
      <Form.Item<BeneficiaryFormType>
        name="descriptionProjet"
        label={t('descriptionProjet')}
        required
        rules={getFieldRules(t, { required: true })}
      >
        <Input.TextArea
          rows={4}
          placeholder={t('descriptionProjet_placeholder')}
        />
      </Form.Item>
      <div className="mb-6">
        <p className="text-sm font-medium mb-2">{t('files_title')}</p>
        <Form.Item<BeneficiaryFormType>
          name="files"
          valuePropName="fileList"
          getValueFromEvent={getValueFromEvent}
          className="mb-0"
        >
          <InlineFileUpload
            browseLabel={t('files_browse')}
            maxSizeLabel={t('files_max_size', { size: MAX_UPLOAD_FILESIZE })}
            multiple
            maxCount={10}
          />
        </Form.Item>
      </div>
    </Form>
  );
}
